// Captures the `?ref=<ambassador>` URL parameter on any page and stores it in
// localStorage as `tm_ambassador` with a 15-day TTL, refreshed only when a new
// ?ref= arrives. If the user lands with a new ?ref= while a previous value is
// still stored, the value is overwritten and the TTL is reset.

const STORAGE_KEY = "tm_ambassador";
const URL_PARAM_NAME = "ref";
const TTL_DAYS = 15;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
// Mirror of Ambassador::sanitizeRef on the PHP side.
const VALID_REF_REGEX = /^[A-Za-z0-9._-]{1,50}$/;

type StoredAmbassadorRef = {
  value: string;
  expiresAt: number;
};

function isStorageAvailable(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch {
    return false;
  }
}

function sanitize(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  return VALID_REF_REGEX.test(trimmed) ? trimmed : null;
}

export function getAmbassadorRef(): string | null {
  if (!isStorageAvailable()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredAmbassadorRef;
    if (!parsed || typeof parsed.value !== "string" || typeof parsed.expiresAt !== "number") {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return sanitize(parsed.value);
  } catch {
    return null;
  }
}

export function setAmbassadorRef(value: string): void {
  if (!isStorageAvailable()) {
    return;
  }
  const sanitized = sanitize(value);
  if (sanitized === null) {
    return;
  }
  const payload: StoredAmbassadorRef = {
    value: sanitized,
    expiresAt: Date.now() + TTL_MS,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full / disabled — fail silently.
  }
}

export function clearAmbassadorRef(): void {
  if (!isStorageAvailable()) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

function captureFromUrl(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(URL_PARAM_NAME);
    if (fromUrl === null) {
      return;
    }
    const sanitized = sanitize(fromUrl);
    if (sanitized === null) {
      return;
    }
    // Always overwrite and reset TTL when a new ?ref= lands.
    setAmbassadorRef(sanitized);
  } catch {
    // Intentionally noop.
  }
}

// Run on module import so it executes as early as possible on every page.
captureFromUrl();
